import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { CloudRain, Droplets, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export function PrecipitationHistory({ lat, lon, isDarkMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!lat || !lon) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/weather/enhanced/precipitation-history`, { params: { lat, lon } });
      setData(res.data);
    } catch (err) {
      console.error("Precip history error:", err.message);
      setError("Precipitation history is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  }, [lat, lon]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-white/50" data-testid="precip-history-loading">
        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
        Loading precipitation history...
      </div>
    );
  }

  if (error) {
    return <div role="alert" className="text-sm text-amber-300" data-testid="precip-history-error">{error}</div>;
  }

  if (!data || !data.days?.length) {
    return <div className="text-sm text-white/45" data-testid="precip-history-empty">No precipitation history available yet.</div>;
  }

  const { days, summary } = data;
  const sourceComparison = data.source_comparison;
  const maxPrecip = Math.max(...days.map(d => d.precipitation_in), 0.1);

  return (
    <div data-testid="precip-history-chart">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="font-semibold text-white/80 flex items-center gap-2 text-base leading-tight">
          <CloudRain className="w-4 h-4 text-blue-400" />
          7-Day Precipitation
        </h3>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {sourceComparison && (
            <span className="text-[10px] text-cyan-300 leading-tight" data-testid="precip-source-comparison-badge">
              {sourceComparison.agreement}
            </span>
          )}
          <span className={`text-xs ${isDarkMode ? "text-white/40" : "text-gray-500"}`} data-testid="precip-history-total">
            Total: <strong className="text-blue-400">{summary.total_in}&quot;</strong>
          </span>
          <span className={`text-xs ${isDarkMode ? "text-white/40" : "text-gray-500"}`} data-testid="precip-history-rain-days">
            {summary.rain_days} rain / {summary.dry_days} dry
          </span>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="flex items-end gap-2 h-36 mb-6">
        {days.map((day) => {
          const height = maxPrecip > 0 ? (day.precipitation_in / maxPrecip) * 100 : 0;
          const hasSnow = day.snow_in > 0;
          const hasRain = day.rain_in > 0;

          return (
            <div key={`precip-${day.date}`} className="flex-1 h-full flex items-end justify-center group relative">
              {/* Tooltip on hover */}
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                <div className={`rounded-lg px-2.5 py-1.5 text-[10px] whitespace-nowrap ${isDarkMode ? "bg-black/90 border border-white/10" : "bg-white shadow-lg border border-gray-200"}`}>
                  <p className="font-bold">{day.precipitation_in}&quot; total</p>
                  {hasRain && <p className="text-blue-400">Rain: {day.rain_in}&quot;</p>}
                  {hasSnow && <p className="text-cyan-300">Snow: {day.snow_in}&quot;</p>}
                  <p className={isDarkMode ? "text-white/30" : "text-gray-400"}>{day.precip_hours}h of precip</p>
                </div>
              </div>

              {/* Bar */}
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${Math.max(height, 2)}%` }}
                transition={{ duration: 0.6, delay: days.indexOf(day) * 0.08 }}
                className={`w-full rounded-t-lg cursor-pointer transition-all group-hover:opacity-90 ${
                  day.precipitation_in === 0
                    ? isDarkMode ? "bg-white/5" : "bg-gray-100"
                    : hasSnow
                      ? "bg-gradient-to-t from-cyan-500/60 to-cyan-300/40"
                      : "bg-gradient-to-t from-blue-600/70 to-blue-400/50"
                }`}
                style={{ minHeight: day.precipitation_in > 0 ? "8px" : "2px" }}
                data-testid={`precip-history-bar-${day.date}`}
              />

              {/* Amount label */}
              {day.precipitation_in > 0 && (
                <span className={`absolute -bottom-4 text-[9px] font-mono ${isDarkMode ? "text-blue-400/70" : "text-blue-600"}`}>
                  {day.precipitation_in}&quot;
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Day Labels */}
      <div className="flex gap-2">
        {days.map((day) => (
          <div key={`label-${day.date}`} className="flex-1 text-center">
            <p className={`text-[10px] font-bold ${isDarkMode ? "text-white/50" : "text-gray-600"}`}>{day.day_label}</p>
            <p className={`text-[9px] ${isDarkMode ? "text-white/25" : "text-gray-300"}`}>{day.day_short}</p>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className={`flex items-center justify-between mt-4 pt-3 border-t text-xs ${isDarkMode ? "border-white/5 text-white/40" : "border-gray-100 text-gray-500"}`}>
        <div className="flex items-center gap-1" data-testid="precip-history-daily-average">
          <Droplets className="w-3 h-3 text-blue-400" />
          Avg: {summary.daily_avg_in}&quot;/day
        </div>
        {summary.wettest && summary.wettest.amount > 0 && (
          <div data-testid="precip-history-wettest-day">
            Wettest: {new Date(summary.wettest.date + "T12:00").toLocaleDateString("en-US", { weekday: "short" })} ({summary.wettest.amount}&quot;)
          </div>
        )}
        <div className={`text-[10px] ${isDarkMode ? "text-white/20" : "text-gray-300"}`} data-testid="precip-source-comparison-note">
          {sourceComparison?.confidence_note || "Source: Open-Meteo Archive"}
        </div>
      </div>
    </div>
  );
}
